import React, { FC, useEffect } from "react";
import classNames from "classnames";

import { LocationDto } from "@apiModels/LocationDto.csharp";

import { useInsuranceFilters } from "../Utils/Context/filtersContext";
import { useInsuranceLabels } from "../Utils/Context/labelsContext";
import { filterLocations } from "../Utils/Filters/filterLocations";
import { UrlHandler } from "../Utils/UrlHandler";

import { Combobox } from "../Partials/Combobox";
import { PartialsBackgroundVariants } from "../Partials/PartialsBackgroundVariants";
import { StreamLineIcon } from "../../SvgIcon/StreamLineIcon";

interface LocationsComboboxProps {
  locations: LocationDto[];
  getDataFromUrl?: boolean;
  variant?: PartialsBackgroundVariants;
  className?: string;
}

export const LocationsCombobox: FC<LocationsComboboxProps> = (props) => {
  const {
    locations: locationsList,
    getDataFromUrl = true,
    variant,
    className,
  } = props;

  const { locations, specialization, service, setLocations } =
    useInsuranceFilters();

  const { inputs } = useInsuranceLabels();

  useEffect(() => {
    if (getDataFromUrl) {
      const foundLocations = UrlHandler.getEntryFromUrlQuery<LocationDto>(
        locationsList,
        "locationIds"
      );
      const foundCenters = UrlHandler.getEntryFromUrlQuery<LocationDto>(
        locationsList,
        "centerIds"
      );

      setLocations([
        ...(foundLocations ? ([foundLocations].flat() as LocationDto[]) : []),
        ...(foundCenters ? ([foundCenters].flat() as LocationDto[]) : []),
      ]);
    }
  }, []);

  const filteredLocations = filterLocations(
    {
      specialization,
      service,
    },
    locationsList
  );

  const inputChange = (location: LocationDto, inputValue?: string) => {
    if (!inputValue) return true;
    return location.name.toLowerCase().includes(inputValue.toLowerCase());
  };

  const selectionChange = (selectedLocations: LocationDto[] | null) => {
    setLocations(selectedLocations);
  };

  const renderLocation = (location: LocationDto) => (
    <span
      className={classNames("locationCombobox__item", {
        "locationCombobox__item--rootLocation": location.isLocation,
      })}
    >
      {location.name}
    </span>
  );

  const locationToString = (location: LocationDto | null) =>
    location?.name ?? "";

  const MapPinIcon = () => (
    <span className="locationCombobox__icon">
      <StreamLineIcon iconName="s-pin" />
    </span>
  );

  return (
    <div className={classNames("locationCombobox", className)}>
      <Combobox<LocationDto>
        label={inputs.locationLabel}
        placeholder={inputs.locationPlaceholder}
        multipleSelectPlaceholder={inputs.locationPlaceholder}
        items={filteredLocations}
        currentItem={locations}
        onSelectionChangeArray={selectionChange}
        onInputValueChange={inputChange}
        renderItemInList={renderLocation}
        itemToString={locationToString}
        prefixIcon={<MapPinIcon />}
        className="locationCombobox__select"
        backgroundVariant={variant}
        allowMultipleSelect
      />
    </div>
  );
};
